import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { authApi, sessionsApi } from '@/api/endpoints';
import { WeekCalendar } from '@/components/shared/week-calendar';
import { BillingModeBadge } from '@/components/shared/billing-mode-badge';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/sessoes/')({ component: SessoesIndexPage });

type Status = 'PENDING' | 'CONFIRMED' | 'DONE' | 'DISPUTED' | 'CANCELLED';

const STATUS_LABELS: Record<Status, string> = {
  PENDING: 'Aguardando',
  CONFIRMED: 'Confirmada',
  DONE: 'Concluída',
  DISPUTED: 'Em revisão',
  CANCELLED: 'Cancelada',
};
const STATUS_STYLES: Record<Status, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED: 'bg-teal-50 text-teal-700 border-teal-200',
  DONE: 'bg-slate-100 text-slate-600 border-slate-200',
  DISPUTED: 'bg-orange-50 text-orange-700 border-orange-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};
const FILTERS: { key: Status | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PENDING', label: 'Aguardando' },
  { key: 'CONFIRMED', label: 'Confirmadas' },
  { key: 'DONE', label: 'Concluídas' },
  { key: 'CANCELLED', label: 'Canceladas' },
];

function SessoesIndexPage() {
  const navigate = useNavigate();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isTherapist = !!me?.therapistProfile;

  const [filter, setFilter] = useState<Status | 'ALL'>('ALL');
  const [view, setView] = useState<'list' | 'calendar'>('list');

  const { data: all = [], isLoading } = useQuery({ queryKey: ['sessions'], queryFn: () => sessionsApi.list() });
  const sessions = ((filter === 'ALL' ? all : (all as any[]).filter((s) => s.status === filter)) as any[])
    .slice()
    .sort((a, b) => new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime());

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Sessões</h1>
          <p className="text-slate-500 text-sm mt-1">Histórico e próximos agendamentos</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isTherapist && (
            <>
              <Link to="/dashboard/sessoes/recorrencias" className="text-sm text-teal-600 hover:underline font-medium">
                Ver séries →
              </Link>
              <Link
                to="/dashboard/sessoes/recorrencia/nova"
                className="inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-3 py-2 rounded-xl transition shadow-sm"
              >
                + Nova série
              </Link>
            </>
          )}
          <div className="inline-flex p-1 bg-slate-100 rounded-lg text-sm">
            <button
              onClick={() => setView('calendar')}
              className={cn(
                'px-3 py-1.5 rounded-md font-medium transition',
                view === 'calendar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              📅 Calendário
            </button>
            <button
              onClick={() => setView('list')}
              className={cn(
                'px-3 py-1.5 rounded-md font-medium transition',
                view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
              )}
            >
              ☰ Lista
            </button>
          </div>
        </div>
      </div>

      {view === 'list' && (
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-4 py-1.5 rounded-full text-sm font-medium transition border',
                filter === f.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {view === 'calendar' ? (
        <WeekCalendar perspective={isTherapist ? 'THERAPIST' : 'CLIENT'} />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left px-6 py-3.5 w-[36%]">Pessoa</th>
                <th className="text-left px-6 py-3.5 w-[28%]">Data · Modalidade</th>
                <th className="text-left px-6 py-3.5 w-[24%]">Status</th>
                <th className="text-right px-6 py-3.5 w-[12%]">Ação</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={4} className="p-12 text-center text-sm text-slate-400">Carregando...</td></tr>
              ) : sessions.length === 0 ? (
                <tr><td colSpan={4} className="p-12 text-center text-sm text-slate-400">Nenhuma sessão encontrada.</td></tr>
              ) : (
                sessions.map((s: any) => {
                  const isPaid =
                    s.payment?.status === 'PAID' ||
                    (s.recurringSchedule?.paymentMode === 'UPFRONT_BATCH' && (s.recurringSchedule?.payments?.length ?? 0) > 0);
                  const hasPending = s.payment && s.payment.status !== 'PAID' && !isPaid;
                  const otherProfile = isTherapist ? s.client : s.therapist;
                  const otherUser = otherProfile?.user;
                  // Prioriza avatarUrl (upload manual) sobre user.image (Google)
                  const otherAvatar = otherProfile?.avatarUrl ?? otherUser?.image;
                  const dimmed = s.status === 'CANCELLED' || s.status === 'DONE';

                  return (
                    <tr
                      key={s.id}
                      onClick={() => navigate({ to: '/dashboard/sessoes/$id', params: { id: s.id } })}
                      className={cn('cursor-pointer hover:bg-slate-50/60 transition border-b border-slate-100 last:border-0', dimmed && 'opacity-70')}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {otherAvatar ? (
                            <img src={otherAvatar} alt="" className="size-9 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="size-9 rounded-full bg-teal-700 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {(otherUser?.name ?? '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-medium text-slate-800 truncate">{otherUser?.name ?? '—'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-700">
                          {new Date(s.scheduledAt).toLocaleString('pt-BR', {
                            weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                          }).replace(',', '.,')}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {s.modality === 'ONLINE' ? 'Online' : s.modality === 'PRESENCIAL' ? 'Presencial' : 'Híbrido'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={cn('text-[11px] px-2.5 py-0.5 rounded-full border', STATUS_STYLES[s.status as Status] ?? 'bg-slate-100 text-slate-600 border-slate-200')}>
                            {STATUS_LABELS[s.status as Status] ?? s.status}
                          </span>
                          <BillingModeBadge mode={s.billingMode} compact />
                          {isPaid && <span className="text-[11px] text-teal-600">Pago</span>}
                          {hasPending && <span className="text-[11px] text-amber-600">Pagamento pendente</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-semibold text-teal-600 whitespace-nowrap">Ver →</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
